// Business templates: each defines which operational tools (tables/tabs) live
// inside an entity of that type. Single source of truth for entity icons & tools.
import {
  Building2, Home, ShoppingCart, Briefcase, Megaphone, User, LayoutGrid, UserRound,
} from "lucide-react";

const L = (fr, en) => ({ fr, en });

export const TEMPLATES = [
  {
    key: "holding",
    icon: Building2,
    color: "#2563EB",
    tables: ["holdings", "tasks", "documents"],
    label: L("Holding", "Holding"),
    desc: L("Suivi de portefeuille, participations et trésorerie du groupe.",
            "Investment portfolio, participations and group cash tracking."),
  },
  {
    key: "sci",
    icon: Home,
    color: "#F59E0B",
    tables: ["properties", "tenants", "rents", "loans", "maintenance", "tasks", "documents"],
    label: L("Immobilier", "Real estate"),
    desc: L("Biens, locataires, loyers, prêts et maintenance.",
            "Properties, tenants, rents, loans and maintenance."),
  },
  {
    key: "ecommerce",
    icon: ShoppingCart,
    color: "#EA580C",
    tables: ["products", "orders", "suppliers", "campaigns", "tasks", "documents"],
    label: L("E-commerce", "Ecommerce"),
    desc: L("Produits, commandes, fournisseurs, marges et campagnes.",
            "Products, orders, suppliers, margins and campaigns."),
  },
  {
    key: "consulting",
    icon: Briefcase,
    color: "#10B981",
    tables: ["invoices", "tasks", "documents"],
    label: L("Conseil", "Consulting"),
    desc: L("Clients, projets, missions et facturation.",
            "Clients, projects, engagements and invoicing."),
  },
  {
    key: "agency",
    icon: Megaphone,
    color: "#8B5CF6",
    tables: ["invoices", "campaigns", "tasks", "documents"],
    label: L("Agence", "Agency"),
    desc: L("Projets clients, campagnes et facturation.",
            "Client projects, campaigns and invoicing."),
  },
  {
    key: "freelance",
    icon: User,
    color: "#06B6D4",
    tables: ["invoices", "tasks", "documents"],
    label: L("Freelance", "Freelancer"),
    desc: L("Missions, facturation et suivi simple en solo.",
            "Engagements, invoicing and simple solo tracking."),
  },
  {
    key: "personal",
    icon: UserRound,
    color: "#EC4899",
    tables: ["holdings", "documents"],
    label: L("Personnel", "Personal"),
    desc: L("Finances perso : patrimoine, budget et documents. Créez des espaces (maison, travail…).",
            "Personal finances: net worth, budget and documents. Build spaces (home, work…)."),
    personal: true,
  },
  {
    key: "custom",
    icon: LayoutGrid,
    color: "#64748B",
    tables: ["tasks", "documents"],
    label: L("Personnalisé", "Custom"),
    desc: L("Démarrez vide avec tâches et documents, ajoutez vos outils.",
            "Start blank with tasks and documents, add your own tools."),
  },
];

const BY_KEY = Object.fromEntries(TEMPLATES.map((t) => [t.key, t]));

export function getTemplate(key) {
  return BY_KEY[key] || BY_KEY.custom;
}
export function getEntityIcon(type) {
  return getTemplate(type).icon;
}
export function getEntityTables(type) {
  return getTemplate(type).tables;
}

// All resources that can be enabled as entity sections (tools/tabs), in display order.
// "tasks" and "projects" are intentionally excluded — work now lives on Boards inside
// Spaces projects, and the legacy generic "projects" table has been retired.
export const ALL_SECTIONS = [
  "holdings", "properties", "tenants", "rents", "loans", "maintenance",
  "products", "orders", "suppliers", "campaigns", "invoices",
  "documents",
];

// Resolve the actual tabs for an entity: custom 'tables' override the template default.
// Legacy "tasks" sections are filtered out (work is now on Boards).
export function resolveEntityTables(entity) {
  if (entity && Array.isArray(entity.tables) && entity.tables.length) {
    return ALL_SECTIONS.filter((s) => entity.tables.includes(s));
  }
  return getEntityTables(entity?.type).filter((s) => s !== "tasks");
}

export const COUNTRIES = [
  {
    code: "FR",
    label: L("France", "France"),
    legal_forms: [
      "SCI", "SASU", "SAS", "SARL", "EURL",
      "Micro-entreprise", "Entreprise individuelle (EI)", "SA", "Holding (SAS)",
    ],
    tax_regimes: ["IR — Impôt sur le revenu", "IS — Impôt sur les sociétés"],
  },
  {
    code: "US",
    label: L("États-Unis", "United States"),
    legal_forms: [
      "LLC", "Single-member LLC", "S-Corporation", "C-Corporation",
      "Sole Proprietorship", "Partnership",
    ],
    tax_regimes: ["Pass-through", "Corporate (C-Corp)"],
  },
];

export function getCountry(code) {
  return COUNTRIES.find((c) => c.code === code);
}

export const COLOR_SWATCHES = [
  "#2563EB", "#10B981", "#EA580C", "#F59E0B", "#8B5CF6",
  "#06B6D4", "#EF4444", "#EC4899", "#64748B", "#84CC16",
];

// ---------------------------------------------------------------------------
// Spaces — functional areas inside an entity (Operately-style). A curated
// library + sensible defaults per business template.
// ---------------------------------------------------------------------------
import {
  Wallet, Settings, TrendingUp, Package, Scale, LineChart, Users,
} from "lucide-react";

export const SPACE_ICONS = {
  "layout-grid": LayoutGrid, wallet: Wallet, settings: Settings,
  megaphone: Megaphone, "trending-up": TrendingUp, package: Package,
  scale: Scale, "line-chart": LineChart, home: Home, briefcase: Briefcase,
  users: Users, building: Building2, cart: ShoppingCart,
};

export function getSpaceIcon(name) {
  return SPACE_ICONS[name] || LayoutGrid;
}

export const SPACES_LIBRARY = [
  { key: "general", icon: "layout-grid", label: L("Général", "General"),
    desc: L("Annonces et ressources transverses.", "Org-wide announcements and resources.") },
  { key: "finance", icon: "wallet", label: L("Finance", "Finance"),
    desc: L("Budget, comptabilité et trésorerie.", "Budget, accounting and cash.") },
  { key: "operations", icon: "settings", label: L("Opérations", "Operations"),
    desc: L("Le quotidien pour que les autres avancent.", "Day-to-day so everyone can focus.") },
  { key: "marketing", icon: "megaphone", label: L("Marketing", "Marketing"),
    desc: L("Notoriété, contenu et acquisition.", "Awareness, content and acquisition.") },
  { key: "sales", icon: "trending-up", label: L("Ventes", "Sales"),
    desc: L("Prospection, deals et revenus.", "Prospecting, deals and revenue.") },
  { key: "product", icon: "package", label: L("Produit", "Product"),
    desc: L("Quoi construire et pourquoi.", "What to build next and why.") },
  { key: "legal", icon: "scale", label: L("Juridique", "Legal"),
    desc: L("Contrats, conformité et risques.", "Contracts, compliance and risk.") },
  { key: "investments", icon: "line-chart", label: L("Investissements", "Investments"),
    desc: L("Portefeuille et participations.", "Portfolio and participations.") },
  { key: "properties", icon: "home", label: L("Immobilier", "Properties"),
    desc: L("Biens, locataires et travaux.", "Assets, tenants and works.") },
  { key: "delivery", icon: "briefcase", label: L("Delivery", "Delivery"),
    desc: L("Missions et livrables clients.", "Client engagements and delivery.") },
  { key: "hr", icon: "users", label: L("RH", "HR"),
    desc: L("Recrutement et équipe.", "Hiring and team.") },
];

export const SPACES_BY_KEY = Object.fromEntries(SPACES_LIBRARY.map((s) => [s.key, s]));

const TEMPLATE_SPACES = {
  holding: ["investments", "finance", "legal", "general"],
  sci: ["properties", "finance", "legal", "general"],
  ecommerce: ["marketing", "operations", "product", "finance", "general"],
  consulting: ["general", "sales", "legal", "product"],
  agency: ["marketing", "delivery", "sales", "general"],
  freelance: ["delivery", "finance", "general"],
  personal: ["finance", "general"],
  custom: ["general"],
};

export function getTemplateSpaceKeys(type) {
  return TEMPLATE_SPACES[type] || TEMPLATE_SPACES.custom;
}
